import { REQUEST_ID_HEADER, newRequestId } from '@/api/request-id'
import { getAccessToken } from '@/lib/auth/store'
import { isTokenSpent, sendableAccessToken } from '@/lib/auth/token-expiry'
import { config } from '@/lib/config'

/**
 * The engine session, in a production build: the signed-in member's mm-be
 * access token is shown to the engine ONCE, at `POST /v1/session`, and the short engine token it
 * answers with (thirty minutes) is what every Mitra call carries after that.
 * The same exchange the mobile app makes.
 *
 * WHY. Forwarding the mm-be token on every call puts a credential that is
 * good for the whole platform through every hop of the engine, request after
 * request; an engine token is good for Mitra alone, lives minutes, and can be
 * withdrawn by the engine.
 *
 * Held in memory only, never in storage: a reload costs one exchange. Bound
 * to the mm-be token it was exchanged from, so a sign-out, another member
 * signing in or a renewed mm-be token is never answered with the old one.
 *
 * A deployment that offers no exchange answers 404 (an engine that verifies
 * tokens offline, such as a local one). Only then does the member's mm-be
 * token go to the engine directly, as it did before, and the exchange is not
 * asked for again in this page's lifetime. Every other refusal is handed
 * back to the caller as that status, never answered by sending the platform
 * token anyway.
 */

/**
 * PRODUCTION BUILDS ONLY. Staging and local builds send the member's mm-be
 * token to the engine directly, as they always have (owner, 2026-10-30: the
 * token flow goes into the production release). `import.meta.env.VITE_ENV`
 * is replaced with a literal at build time, so this is a constant in a
 * bundle.
 */
const EXCHANGE = import.meta.env.VITE_ENV === 'production'

/** What a Mitra call carries. */
export type EngineBearer =
  /** An engine session token. A 401 on it is answered by exchanging again. */
  | { kind: 'session'; token: string }
  /** The member's mm-be token (no exchange on this engine), or null when signed out. */
  | { kind: 'direct'; token: string | null }
  /**
   * The exchange refused (401, 403) or failed (5xx, malformed grant): its
   * status is the answer, with the engine's `code` when it gave one (a 403
   * `not_in_rollout` for a reader outside the rollout).
   */
  | { kind: 'refused'; status: number; code?: string }

interface Session {
  token: string
  /** Epoch ms after which the token is exchanged again before use. */
  renewAt: number
  /** The mm-be token it was exchanged from. */
  from: string
}

/** Renewed this long before it lapses, so a token never dies in flight. */
const RENEW_SKEW_S = 60

/** One round trip that does nothing but check an identity. */
const EXCHANGE_TIMEOUT_MS = 10_000

let session: Session | null = null
let inFlight: { from: string; grant: Promise<EngineBearer> } | null = null
/** Set by a 404: this engine offers no exchange. A fact about the deployment, kept for the page. */
let unavailable = false

/**
 * The bearer to send NOW, with nothing awaited, or `undefined` when one has
 * to be fetched first (`engineBearer`). Known without waiting when a fresh
 * session token for the current member is held, when nobody is signed in,
 * and on an engine with no exchange while the member's token is unspent.
 */
export function readyEngineBearer(): EngineBearer | undefined {
  const member = getAccessToken()
  if (member === null) return { kind: 'direct', token: null }
  if (!EXCHANGE || unavailable) {
    return isTokenSpent(member) ? undefined : { kind: 'direct', token: member }
  }
  if (session !== null && session.from === member && Date.now() < session.renewAt) {
    return { kind: 'session', token: session.token }
  }
  return undefined
}

/**
 * The bearer for a Mitra call, exchanging first when no fresh session token
 * is held. Concurrent callers share one exchange. Rejects only when the
 * exchange got no answer at all (offline, a timeout), the way the call itself
 * would have.
 */
export async function engineBearer(): Promise<EngineBearer> {
  const ready = readyEngineBearer()
  if (ready !== undefined) return ready
  // Renewed first when its own `exp` has passed: a spent token is one the
  // exchange can only refuse.
  const member = await sendableAccessToken()
  if (member === null) return { kind: 'direct', token: null }
  if (!EXCHANGE || unavailable) return { kind: 'direct', token: member }
  if (session !== null && session.from === member && Date.now() < session.renewAt) {
    return { kind: 'session', token: session.token }
  }
  if (inFlight !== null && inFlight.from === member) return inFlight.grant
  const entry = { from: member, grant: exchange(member) }
  inFlight = entry
  entry.grant
    .finally(() => {
      if (inFlight === entry) inFlight = null
    })
    .catch(() => undefined)
  return entry.grant
}

/**
 * Drops a session token the engine refused, so the next call exchanges
 * again. Only that token: when another 401 already replaced it, nothing
 * happens, so concurrent refusals cost one exchange.
 */
export function dropEngineSession(token: string): void {
  if (session?.token === token) session = null
}

/** Tests only: forget everything, including that the engine offers no exchange. */
export function resetEngineSession(): void {
  session = null
  inFlight = null
  unavailable = false
}

async function exchange(member: string): Promise<EngineBearer> {
  const prefix = config.mitraPrefix
  if (prefix === null) return { kind: 'direct', token: member }
  const response = await fetch(`${prefix}/v1/session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${member}`, [REQUEST_ID_HEADER]: newRequestId() },
    signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS),
  })
  if (response.status === 404) {
    unavailable = true
    session = null
    return { kind: 'direct', token: member }
  }
  if (!response.ok) {
    const code = response.status === 403 ? await readCode(response) : null
    return code === null
      ? { kind: 'refused', status: response.status }
      : { kind: 'refused', status: response.status, code }
  }
  const grant = await readGrant(response)
  // A 200 without a usable token is the engine's fault, not the member's.
  if (grant === null) return { kind: 'refused', status: 502 }
  // The member signed out, or was replaced, while this was in flight: the
  // grant is theirs, so it is not kept, and the bearer is decided afresh.
  if (getAccessToken() !== member) return engineBearer()
  const kept = Math.max(grant.expiresIn - RENEW_SKEW_S, Math.floor(grant.expiresIn / 2))
  session = { token: grant.token, renewAt: Date.now() + kept * 1000, from: member }
  return { kind: 'session', token: grant.token }
}

/** A refusal's `code` (`{"code": "not_in_rollout"}`), or null when it carries none. */
async function readCode(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json()
    if (typeof body !== 'object' || body === null) return null
    const { code } = body as Record<string, unknown>
    return typeof code === 'string' ? code : null
  } catch {
    return null
  }
}

async function readGrant(response: Response): Promise<{ token: string; expiresIn: number } | null> {
  try {
    const body: unknown = await response.json()
    if (typeof body !== 'object' || body === null) return null
    const { token, expires_in: expiresIn } = body as Record<string, unknown>
    if (typeof token !== 'string' || token === '') return null
    if (typeof expiresIn !== 'number' || !Number.isFinite(expiresIn) || expiresIn <= 0) return null
    return { token, expiresIn }
  } catch {
    return null
  }
}
